import { useState, useEffect, useRef } from "react";
import {
  motion, AnimatePresence,
  useScroll, useTransform, useSpring, useMotionValue,
} from "framer-motion";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";

// Authored rhythm — not a metronome
const PACING = [3000, 2600, 3500, 2800, 3200];

export default function BoldStatement() {
  const [idx, setIdx]       = useState(0);
  const [glowPos, setGlowPos] = useState({ x: 50, y: 50 });
  const barRef              = useRef(null);
  const sectionRef          = useRef(null);
  const wordIndexRef        = useRef(0);
  const startTimeRef        = useRef(Date.now());
  const { lang }            = useLang();
  const WORDS               = c.bold.words[lang];

  // ── Variable-paced RAF loop ────────────────────────────────────
  useEffect(() => {
    let rafId;
    const tick = () => {
      const interval = PACING[wordIndexRef.current % PACING.length];
      const progress = Math.min((Date.now() - startTimeRef.current) / interval, 1);
      if (barRef.current) barRef.current.style.transform = `scaleX(${progress})`;
      if (progress >= 1) {
        wordIndexRef.current = (wordIndexRef.current + 1) % WORDS.length;
        setIdx(wordIndexRef.current);
        startTimeRef.current = Date.now();
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [WORDS.length]);

  // ── Scroll layers ──────────────────────────────────────────────
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const wordScale = useSpring(
    useTransform(scrollYProgress, [0, 0.22, 0.78, 1], [0.88, 1.0, 1.0, 0.93]),
    { stiffness: 52, damping: 26 }
  );
  const ghostY = useSpring(
    useTransform(scrollYProgress, [0, 1], [55, -110]),
    { stiffness: 30, damping: 20 }
  );
  const topY = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, -26]),
    { stiffness: 36, damping: 22 }
  );
  const botY = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 26]),
    { stiffness: 36, damping: 22 }
  );

  // ── Mouse drift on word (Framer Motion spring) ─────────────────
  const rawDriftX = useMotionValue(0);
  const rawDriftY = useMotionValue(0);
  const driftX    = useSpring(rawDriftX, { stiffness: 40, damping: 22 });
  const driftY    = useSpring(rawDriftY, { stiffness: 40, damping: 22 });

  const handleMouseMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    // Glow follows cursor (CSS-transitioned)
    setGlowPos({
      x: ((e.clientX - r.left) / r.width)  * 100,
      y: ((e.clientY - r.top)  / r.height) * 100,
    });
    // Word drifts very subtly (±6px / ±3px)
    rawDriftX.set(((e.clientX - r.left) / r.width  - 0.5) * 12);
    rawDriftY.set(((e.clientY - r.top)  / r.height - 0.5) *  6);
  };

  return (
    <section
      ref={sectionRef}
      id="bold-statement"
      className="relative overflow-hidden"
      style={{
        background: "#F4F0EA",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      onMouseMove={handleMouseMove}
    >
      {/* ── Grain ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0, opacity: 0.028, pointerEvents: "none",
          backgroundImage: "url(\"data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='160' height='160' filter='url(%23g)'/></svg>\")",
          backgroundSize: "160px 160px",
        }}
      />

      {/* ── Breathing ambient — amber (top-left) ── */}
      <div
        aria-hidden="true"
        className="bs-breathe-a"
        style={{
          position: "absolute", pointerEvents: "none",
          top: "-12%", left: "-8%",
          width: 780, height: 780, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,121,65,0.085) 0%, transparent 60%)",
          filter: "blur(72px)",
        }}
      />

      {/* ── Breathing ambient — ice (bottom-right) ── */}
      <div
        aria-hidden="true"
        className="bs-breathe-b"
        style={{
          position: "absolute", pointerEvents: "none",
          bottom: "-8%", right: "-10%",
          width: 660, height: 660, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(124,196,216,0.060) 0%, transparent 60%)",
          filter: "blur(72px)",
        }}
      />

      {/* ── Mouse-following light ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", pointerEvents: "none",
          width: 920, height: 920, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,121,65,0.058) 0%, transparent 55%)",
          filter: "blur(65px)",
          left:  `${glowPos.x}%`,
          top:   `${glowPos.y}%`,
          transform: "translate(-50%, -50%)",
          transition: "left 1.15s cubic-bezier(0.22,1,0.36,1), top 1.15s cubic-bezier(0.22,1,0.36,1)",
        }}
      />

      {/* ── Corner labels — drift up with scroll ── */}
      <motion.div
        aria-hidden="true"
        style={{
          position: "absolute", top: "3rem", left: "5rem", y: topY,
          color: "rgba(26,18,9,0.18)",
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 10, letterSpacing: "0.22em",
          textTransform: "uppercase", userSelect: "none",
        }}
      >
        Przestrzeń Apps · Poznań
      </motion.div>
      <motion.div
        aria-hidden="true"
        style={{
          position: "absolute", top: "3rem", right: "5rem", y: topY,
          color: "rgba(26,18,9,0.18)",
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 10, letterSpacing: "0.22em",
          textTransform: "uppercase", userSelect: "none",
        }}
      >
        2024 – 2026
      </motion.div>

      {/* ── Ghost number — deep parallax ── */}
      <motion.div
        aria-hidden="true"
        style={{
          position: "absolute", bottom: "4rem", right: "5rem", y: ghostY,
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: "clamp(5rem, 16vw, 14rem)", fontWeight: 700,
          color: "rgba(26,18,9,0.038)", lineHeight: 1,
          userSelect: "none", pointerEvents: "none",
        }}
      >
        0{idx + 1}
      </motion.div>

      {/* ── Content ── */}
      <div style={{ position: "relative", zIndex: 1, width: "100%", textAlign: "center" }}>

        {/* Label — drifts up */}
        <motion.p
          style={{ y: topY }}
          /* inline label styles */
        >
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 10, letterSpacing: "0.34em",
            textTransform: "uppercase",
            color: "rgba(26,18,9,0.36)",
            display: "block",
            marginBottom: "2.5rem",
            padding: "0 2rem",
          }}>
            {c.bold.label[lang]}
          </span>
        </motion.p>

        {/* Word — scroll scale + mouse drift */}
        <motion.div style={{ scale: wordScale, x: driftX, y: driftY }}>
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              height: "clamp(6.5rem, 15vw, 13rem)",
              width: "100vw",
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={WORDS[idx]}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                exit={{   y: "-110%" }}
                transition={{ duration: 0.68, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  position: "absolute", inset: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <motion.h2
                  initial={{ opacity: 0, filter: "blur(10px)", scale: 0.96, letterSpacing: "-0.022em" }}
                  animate={{ opacity: 1, filter: "blur(0px)",  scale: 1,    letterSpacing: "0em"      }}
                  exit={{    opacity: 0, filter: "blur(10px)", scale: 1.02                             }}
                  transition={{ duration: 0.68, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontWeight: 300, fontStyle: "italic",
                    fontSize: "clamp(4rem, 10.5vw, 9rem)",
                    color: "#1A1209", lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {WORDS[idx]}
                </motion.h2>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Progress + counter — drifts down */}
        <motion.div style={{ y: botY, padding: "0 2rem" }}>
          <div
            aria-hidden="true"
            style={{
              marginTop: "3rem", position: "relative",
              width: "clamp(8rem, 18vw, 18rem)", height: 1,
              background: "rgba(26,18,9,0.10)",
              borderRadius: 999, overflow: "hidden",
              marginLeft: "auto", marginRight: "auto",
            }}
          >
            <div
              ref={barRef}
              style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(to right, rgba(200,121,65,0.55), #1A1209)",
                transformOrigin: "left", borderRadius: 999,
              }}
            />
          </div>
          <p
            aria-hidden="true"
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 9, letterSpacing: "0.26em",
              color: "rgba(26,18,9,0.25)", marginTop: "1.1rem",
              textTransform: "uppercase",
            }}
          >
            {String(idx + 1).padStart(2, "0")} / {String(WORDS.length).padStart(2, "0")}
          </p>

          <span
            aria-hidden="true"
            style={{
              display: "block",
              marginTop: "2rem",
              fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
              fontWeight: 300,
              fontSize: "clamp(0.9rem, 1.3vw, 1.1rem)",
              letterSpacing: "0.08em",
              background: "linear-gradient(135deg, #C87941 0%, #7CC4D8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              opacity: 0.52,
              userSelect: "none",
              lineHeight: 1,
            }}
          >
            間
          </span>
        </motion.div>

      </div>

      {/* ── Bottom hairline ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 3,
          background: "linear-gradient(to right, transparent 0%, rgba(200,121,65,0.50) 40%, rgba(200,121,65,0.50) 60%, transparent 100%)",
        }}
      />
    </section>
  );
}
